import {useCallback, useMemo, useState} from 'react';

import localStorage from 'LocalStorage';
import {StructuredTableColumnWidthStore} from 'notebook/structured-tables/hooks/useStructuredTableColumnWidths';

/**
 * A {@link StructuredTableColumnWidthStore} that also exposes {@link updateWidths}, a read-modify-write
 * that computes the next record from the value currently in localStorage rather than a cached snapshot.
 * Callers that share a storage key across concurrently-mounted instances (or browser tabs) use this to
 * avoid clobbering keys another instance wrote after this one mounted.
 */
export interface LocalStorageColumnWidthStore extends StructuredTableColumnWidthStore {
  updateWidths: (updater: (currentWidths: Record<string, number>) => Record<string, number>) => void;
}

function readWidths(storageKey: string): Record<string, number> {
  const raw = localStorage.getItem(storageKey);
  if (raw == null) {
    return {};
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    // A foreign or corrupt value under our key is not JSON we wrote, so drop it and start empty.
    // JSON.parse throws only SyntaxError; anything else is a real bug and must surface, so rethrow it.
    if (error instanceof SyntaxError) {
      localStorage.removeItem(storageKey);
      return {};
    }
    throw error;
  }
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    localStorage.removeItem(storageKey);
    return {};
  }
  const widths: Record<string, number> = {};
  for (const [columnId, width] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof width === 'number' && Number.isFinite(width)) {
      widths[columnId] = width;
    }
  }
  return widths;
}

/**
 * A {@link StructuredTableColumnWidthStore} backed by localStorage under `storageKey`, so column
 * widths survive unmounts and reloads and are shared by every table that reads the same key.
 *
 * Widths are keyed by column `provenanceUuid`, so tables sharing a key track the same width for a
 * given column. Choose a key stable enough to persist yet narrow enough that only columns meant to
 * track together share it.
 */
export default function useLocalStorageColumnWidthStore(storageKey: string): LocalStorageColumnWidthStore {
  const [stored, setStored] = useState<{key: string; widthsByColumnId: Record<string, number>}>(() => ({
    key: storageKey,
    widthsByColumnId: readWidths(storageKey),
  }));

  // The state initializer runs only on the first mount, so a changed key would otherwise keep exposing
  // the previous key's widths and write them back under the new key. Reload from the new key instead.
  let widthsByColumnId = stored.widthsByColumnId;
  if (stored.key !== storageKey) {
    widthsByColumnId = readWidths(storageKey);
    setStored({key: storageKey, widthsByColumnId});
  }

  const updateWidths = useCallback(
    (updater: (currentWidths: Record<string, number>) => Record<string, number>): void => {
      // Re-read localStorage here rather than trusting the mount-time snapshot: another instance or tab
      // sharing this key may have written since we mounted, and computing the next record from the stale
      // snapshot would drop their keys when we write the whole record back.
      const nextWidths = updater(readWidths(storageKey));
      setStored({key: storageKey, widthsByColumnId: nextWidths});
      localStorage.json.setItem(storageKey, nextWidths);
    },
    [storageKey]
  );

  const setWidthsByColumnId = useCallback(
    (nextWidths: Record<string, number>): void => {
      updateWidths(() => nextWidths);
    },
    [updateWidths]
  );

  return useMemo(
    () => ({widthsByColumnId, setWidthsByColumnId, updateWidths}),
    [widthsByColumnId, setWidthsByColumnId, updateWidths]
  );
}
